import { useState, useId } from 'react';
import type { ReactNode, ElementType, SyntheticEvent } from 'react';

import { Accordion as MUIAccordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@classify/react-core-components/Box';
import { styled, SxProps, Theme } from '@classify/react-core-components/styles';

import Typography from '../Typography';
import EnrichText from '../EnrichText';

export interface Props {
  items: Array<{
    question: string;
    answer: ReactNode;
    disabled?: boolean;
  }>;
  expandAriaLabel: string;
  sx?: SxProps<Theme>;
  slotProps?: {
    heading?: {
      component?: ElementType;
    };
  };
}

const AccordionStyled = styled(MUIAccordion)(({ theme: { spacing, palette } }) => ({
  '&.MuiAccordion-root': {
    ':before': { backgroundColor: palette.layout.surface.border },
  },
  '& .MuiAccordionSummary-root.Mui-expanded': {
    minHeight: 'auto',
  },
  '& .MuiAccordionSummary-content': {
    margin: spacing(2, 0),
  },
  '& ul': {
    margin: 0,
    paddingLeft: spacing(2),
  },
  '& li': {
    color: palette.text.onMainMinor,
  },
}));

const Accordion = ({ items, expandAriaLabel, sx, slotProps }: Props) => {
  const [expandedPanelId, setExpandedPanelId] = useState<string | null>(null);

  const getOnPanelChangeHandler = (panelId: string) => (e: SyntheticEvent, expanded: boolean) => {
    setExpandedPanelId(expanded ? panelId : null);
  };

  const mainId = useId();

  return (
    <Box sx={sx}>
      {items.map((item, n) => {
        const id = `${mainId}-item-${n}`;
        return (
          <AccordionStyled
            key={id}
            expanded={expandedPanelId === id}
            onChange={getOnPanelChangeHandler(id)}
            disabled={item.disabled}
            slotProps={{ heading: { component: slotProps?.heading?.component || 'div' } }}
          >
            <AccordionSummary
              aria-controls={`content-${id}`}
              id={`header-${id}`}
              expandIcon={<ExpandMoreIcon aria-label={expandAriaLabel} />}
            >
              <Typography component="div" sx={{ color: 'text.onMain' }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <EnrichText>
                <Typography
                  component="div"
                  sx={{ color: 'text.onMainMinor' }}
                  {...(typeof item.answer === 'string'
                    ? {
                        dangerouslySetInnerHTML: { __html: item.answer },
                      }
                    : {
                        children: item.answer,
                      })}
                />
              </EnrichText>
            </AccordionDetails>
          </AccordionStyled>
        );
      })}
    </Box>
  );
};

export default Accordion;
